import type { DocumentationPage } from "@/types/domain";

export interface PageTreeNode extends DocumentationPage {
  children: PageTreeNode[];
}

export function buildPageTree(pages: DocumentationPage[]) {
  const nodes = new Map<string, PageTreeNode>(
    pages.map((page) => [page.id, { ...page, children: [] }]),
  );
  const roots: PageTreeNode[] = [];

  for (const node of nodes.values()) {
    const parent = node.parentPageId ? nodes.get(node.parentPageId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortNodes = (items: PageTreeNode[]) => {
    items.sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
    items.forEach((item) => sortNodes(item.children));
  };
  sortNodes(roots);
  return roots;
}

export function validatePageMove(
  page: Pick<DocumentationPage, "id" | "ancestorIds" | "depth">,
  destination: Pick<DocumentationPage, "id" | "ancestorIds" | "depth"> | null,
  subtreeMaxRelativeDepth: number,
) {
  if (!destination) {
    return { valid: true, newDepth: 0 } as const;
  }
  if (destination.id === page.id || destination.ancestorIds.includes(page.id)) {
    return { valid: false, reason: "A page cannot move inside itself." } as const;
  }
  const newDepth = destination.depth + 1;
  if (newDepth + subtreeMaxRelativeDepth > 4) {
    return {
      valid: false,
      reason: "Pages can be nested up to five levels.",
    } as const;
  }
  return { valid: true, newDepth } as const;
}

export function tokenizeSearch(value: string) {
  return [...new Set(
    value
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .split(/\s+/)
      .filter((token) => token.length >= 2)
      .flatMap((token) => {
        const prefixes: string[] = [];
        for (let index = 2; index <= Math.min(token.length, 12); index += 1) {
          prefixes.push(token.slice(0, index));
        }
        return prefixes;
      }),
  )].slice(0, 100);
}
